import type { Factor, TriangleItem } from "../types/driehoek-data";
import { clampToSector } from "./geometry";

const ITEM_SELECTOR = ".cd-triangle-item";

function elementsOverlap(a: Element, b: Element): boolean {
  const ra = a.getBoundingClientRect();
  const rb = b.getBoundingClientRect();
  return !(
    ra.right < rb.left ||
    ra.left > rb.right ||
    ra.bottom < rb.top ||
    ra.top > rb.bottom
  );
}

/** Iterative DOM collision resolution (ported from playserver driehoek). */
export function resolveCollisions(container: HTMLElement): void {
  const items = container.querySelectorAll(ITEM_SELECTOR);
  if (items.length < 2) return;

  let changed = true;
  let iterations = 0;
  const maxIter = 20;
  const NUDGE = 25;

  while (changed && iterations < maxIter) {
    changed = false;
    iterations++;

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        if (elementsOverlap(items[i], items[j])) {
          const el = items[j] as HTMLElement;
          const factor = el.dataset.factor as Factor;
          let x = parseFloat(el.dataset.x ?? "0");
          let y = parseFloat(el.dataset.y ?? "0");
          x += (Math.random() - 0.5) * NUDGE;
          y += (Math.random() - 0.5) * NUDGE;
          const [cx, cy] = clampToSector(x, y, factor);
          el.style.left = `${(cx / 400) * 100}%`;
          el.style.top = `${(cy / 400) * 100}%`;
          el.dataset.x = String(cx);
          el.dataset.y = String(cy);
          el.style.fontSize = "";
          changed = true;
        }
      }
    }
  }

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (elementsOverlap(items[i], items[j])) {
        [items[i], items[j]].forEach((node) => {
          const el = node as HTMLElement;
          const fs = el.style.fontSize || "0.85rem";
          const num = parseFloat(fs);
          const unit = fs.replace(/[\d.]/g, "") || "rem";
          if (num > 0.6) {
            el.style.fontSize = `${num - 0.05}${unit}`;
          }
        });
      }
    }
  }
}

export function readPositionsFromDOM(
  container: HTMLElement,
  items: TriangleItem[]
): TriangleItem[] {
  const nodes = container.querySelectorAll(ITEM_SELECTOR);
  const byId = new Map<string, { x: number; y: number }>();

  for (const node of nodes) {
    const el = node as HTMLElement;
    const id = el.dataset.id;
    const x = parseFloat(el.dataset.x ?? "");
    const y = parseFloat(el.dataset.y ?? "");
    if (id && !Number.isNaN(x) && !Number.isNaN(y)) {
      byId.set(id, { x, y });
    }
  }

  return items.map((item) => {
    const pos = byId.get(item.id);
    return pos ? { ...item, x: pos.x, y: pos.y } : item;
  });
}

export function itemsPositionsChanged(before: TriangleItem[], after: TriangleItem[]): boolean {
  return after.some((item) => {
    const prev = before.find((p) => p.id === item.id);
    return prev && (prev.x !== item.x || prev.y !== item.y);
  });
}
